import React from 'react';

import { Grid } from '../../../common/components/Grid/Grid';
import {
  GridColumn,
  GridCountFunction,
  GridGetterFunction,
} from '../../../common/components/Grid/GridDataTypes';
import {
  GetClientDto,
  GetClientWithContactsDto,
  GetContactSummaryDto,
} from '../../../common/pokCore/autogenerated/pokApiClient';
import { useTranslation } from '../../../common/hooks/useTranslation';

interface ClientWithContactsSearchProps {
  getData: GridGetterFunction;
  getCount: GridCountFunction;
  onRowClick: (key: string) => Promise<void> | void | undefined;
  refresh?: boolean;
  hideContacts?: boolean;
}

const contactTypeName = (contact: GetContactSummaryDto, lang: string) =>
  (lang === 'PL' ? contact.contactType?.value : contact.contactType?.valueEn) ||
  '';

export const contactsToText = (
  contacts: GetContactSummaryDto[] | undefined,
  lang: string,
) =>
  (contacts ?? [])
    .filter(contact => contact.active)
    .map(contact => `${contactTypeName(contact, lang)}: ${contact.email ?? ''}`)
    .join(' ');

export const renderContacts = (
  contacts: GetContactSummaryDto[] | undefined,
  lang: string,
) => {
  const activeContacts = (contacts ?? []).filter(contact => contact.active);
  if (!activeContacts.length) {
    return '';
  }
  return (
    <>
      {activeContacts.map(contact => (
        <div key={contact.id}>
          {contactTypeName(contact, lang)}: {contact.email}
        </div>
      ))}
    </>
  );
};

export const clientBasicForGrid = (client: GetClientDto) => ({
  key: client.id,
  values: {
    'client.systemNumber': client.systemNumber,
    'client.name': client.name,
    'client.nip': client.nip,
  },
  isInactive: !client.active,
});

export const clientWithContactsForGrid = (
  client: GetClientWithContactsDto,
  lang: string,
) => {
  const record = clientBasicForGrid(client);
  return {
    ...record,
    values: {
      ...record.values,
      'contact.email': renderContacts(client.contacts, lang),
    },
  };
};

export const ClientWithContactsSearch: React.FC<
  ClientWithContactsSearchProps
> = props => {
  const { t, tk } = useTranslation('clientLng');

  const columns: GridColumn[] = [
    { header: t(tk.common.sysNumber), property: 'client.systemNumber' },
    { header: t(tk.common.name), property: 'client.name' },
    { header: t(tk.client.nip), property: 'client.nip' },
    ...(props.hideContacts
      ? []
      : [
          {
            header: t(tk.client.contactsColumn),
            property: 'contact.email',
            noSorting: true,
          },
        ]),
  ];

  return (
    <Grid
      getData={props.getData}
      getDataCount={props.getCount}
      columns={columns}
      showFilter={true}
      withFilterInColumns={true}
      initialOrderColumn="client.name"
      availablePageSizes={[10, 20, 50]}
      emptyGridMessage={
        props.hideContacts
          ? t(tk.client.noClientsWithoutContacts)
          : t(tk.client.noClients)
      }
      onRowClick={props.onRowClick}
      refresh={props.refresh}
      withHistory={true}
    />
  );
};
